<template>
  <aside
    class="z-[99999] shadow-lg h-full absolute bg-white w-72 transition-all duration-300 ease-in-out dark:bg-dark-900"
    :class="[isSidebarOpen ? 'translate-x-0' : '-translate-x-full']"
  >
    <div
      id="sidebar_top"
      class="bg-white px-4 py-3 flex justify-between items-center border-b border-gray-200 dark:bg-dark-900 dark:border-dark-700"
    >
      <img
        src="/logo-erista.png"
        alt="POS Logo"
        class="h-7 w-full object-contain"
      />
      <span
        id="close-sidebar"
        class="cursor-pointer p-1.5 hover:bg-gray-100 rounded-lg transition-colors duration-200 dark:hover:bg-dark-700"
        @click="closeSidebar"
      >
        <ArrowLeftIcon
          class="h-4 w-4 text-gray-600 transition-opacity duration-200 hover:opacity-90 dark:text-dark-300"
        />
      </span>
    </div>
    <div
      id="sidebar_main"
      class="overflow-y-auto px-3 py-4 max-h-[calc(100vh-100px)]"
    >
      <template v-for="item in filteredMenuItems" :key="item.path">
        <RouterLink
          v-if="!item.children"
          :to="item.path"
          :id="item.id"
          class="mb-3 flex items-center px-2 py-1.5 rounded-md text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 group dark:text-dark-200 dark:hover:text-dark-100 dark:hover:bg-dark-800"
          :class="{
            'bg-blue-50 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:bg-blue-900/50 dark:text-blue-100 dark:hover:bg-blue-900/70 dark:hover:text-white':
              isActive(item.path, item),
          }"
          @click="handleMenuItemClick"
        >
          <component
            :is="item.icon"
            class="h-4 w-4 me-2.5 text-gray-500 group-hover:text-gray-700 dark:text-dark-400 dark:group-hover:text-dark-100"
            :class="{
              'text-blue-600 group-hover:text-blue-700 dark:text-blue-200 dark:group-hover:text-white':
                isActive(item.path, item),
            }"
          />
          {{ item.name }}
        </RouterLink>
        <div v-else class="mb-3">
          <div
            class="flex items-center px-2 py-1.5 rounded-md text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 cursor-pointer group dark:text-dark-200 dark:hover:text-dark-100 dark:hover:bg-dark-800"
            @click="toggleMenu(item.id)"
          >
            <component
              :is="item.icon"
              class="h-4 w-4 me-2.5 text-gray-500 group-hover:text-gray-700 dark:text-dark-400 dark:group-hover:text-dark-100"
              :class="{
                'text-blue-600 group-hover:text-blue-700':
                  isActive(item.path, item),
              }"
            />
            {{ item.name }}
            <ChevronDownIcon
              :id="`chevron-down-icon-${item.id}`"
              class="h-4 w-4 ml-auto transition-transform duration-200 text-gray-400 dark:text-dark-400"
              :class="{ 'rotate-180': openMenus[item.id] }"
            />
          </div>
          <div v-show="openMenus[item.id]" class="pl-5">
            <RouterLink
              v-for="child in item.children"
              :key="child.path"
              :to="child.path"
              :id="child.id"
              class="flex mb-1.5 items-center px-2 py-1.5 rounded-md text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 group dark:text-dark-200 dark:hover:text-dark-100 dark:hover:bg-dark-800"
              :class="{
                'bg-blue-50 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:bg-blue-900/50 dark:text-blue-100 dark:hover:bg-blue-900/70 dark:hover:text-white':
                  isActive(child.path, child),
              }"
              @click="handleMenuItemClick"
            >
              <component
                :is="child.icon"
                class="h-4 w-4 me-2.5 text-gray-500 group-hover:text-gray-700 dark:text-dark-400 dark:group-hover:text-dark-100"
                :class="{
                  'text-blue-600 group-hover:text-blue-700 dark:text-blue-200 dark:group-hover:text-white':
                    isActive(child.path, child),
                }"
              />
              {{ child.name }}
            </RouterLink>
          </div>
        </div>
      </template>
    </div>
  </aside>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  watch,
  defineEmits,
  defineExpose,
  onMounted,
  onBeforeUnmount,
} from "vue";
import {
  ArrowLeftIcon,
  HomeIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  BuildingLibraryIcon,
  UsersIcon,
  ChevronDownIcon,
  InboxStackIcon,
  // ChatBubbleLeftRightIcon,
  BookmarkSquareIcon,
  GiftIcon,
  Cog6ToothIcon,
  ListBulletIcon,
  BoltIcon,
  CircleStackIcon,
  ArrowTrendingUpIcon,
  ArrowUpOnSquareIcon,
  ArrowDownOnSquareIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  BackwardIcon,
} from "@heroicons/vue/24/solid";
import { useRoute } from "vue-router";

const emit = defineEmits(["toggleBlur"]);

const isSidebarOpen = ref(false);
const openMenus = reactive({});
const route = useRoute();
const isBlur = ref(false);

const menuItems = [
  {
    path: "/dashboard",
    name: "Beranda",
    id: "url-dashboard",
    icon: HomeIcon,
    permission: ["all"],
  },
  {
    path: "/master",
    name: "Master",
    id: "url-master",
    icon: InboxStackIcon,
    permission: ["1", "2", "3"],
    children: [
      {
        path: "/pelanggan",
        name: "Pelanggan",
        id: "url-customer",
        icon: UsersIcon,
        permission: ["1", "2", "3"],
      },
      {
        path: "/item",
        name: "Item",
        id: "url-item",
        icon: CubeIcon,
        permission: ["1", "2", "3", "4"],
      },
      {
        path: "/kategori-item",
        name: "Kategori Item",
        id: "url-kategori-item",
        icon: BookmarkSquareIcon,
        permission: ["1"],
      },
      {
        path: "/global-discount",
        name: "Global Discount",
        id: "url-global-discount",
        icon: GiftIcon,
        permission: ["1", "2"],
      },
      {
        path: "/unit",
        name: "Unit",
        id: "url-unit",
        icon: BuildingLibraryIcon,
        permission: ["1", "2"],
      },
    ],
  },
  {
    path: "/sales-return",
    name: "Retur Penjualan",
    id: "url-sales-return",
    icon: BackwardIcon,
    permission: ["1", "2"],
  },
  {
    path: "/cash-register",
    name: "Cash Register",
    id: "url-cash-register",
    icon: CurrencyDollarIcon,
    permission: ["1", "2"],
    children: [
      {
        path: "/cash-register",
        name: "Daftar Cash Register",
        id: "url-cash-register",
        icon: ListBulletIcon,
        permission: ["1", "2"],
      },
    ],
  },
  {
    path: "/stock",
    name: "Stok",
    id: "url-stock",
    icon: CircleStackIcon,
    permission: ["1", "2"],
    children: [
      {
        path: "/stock-opname",
        name: "Stok Opname",
        id: "url-stock-opname",
        icon: CircleStackIcon,
        permission: ["1", "2"],
      },
      {
        path: "/inbound-items-form",
        name: "Refill Stok",
        id: "url-inbound-items-form",
        icon: ArrowDownOnSquareIcon,
        permission: ["1", "2", "3"],
      },
      {
        path: "/outbound-items-form",
        name: "Barang Keluar",
        id: "url-outbound-items-form",
        icon: ArrowUpOnSquareIcon,
        permission: ["1", "2"],
      },
    ],
  },
  {
    path: "/riwayat",
    name: "Riwayat",
    id: "url-riwayat",
    icon: ArrowTrendingUpIcon,
    permission: ["1", "2"],
    children: [
      {
        path: "/riwayat-transaksi",
        name: "Riwayat Transaksi",
        id: "url-riwayat-transaksi",
        icon: ClockIcon,
        permission: ["1", "2"],
      },
      {
        path: "/hutang",
        name: "Piutang",
        id: "url-hutang",
        icon: ClockIcon,
        permission: [],
      },
    ],
  },
  {
    path: "/report",
    name: "Laporan",
    id: "url-report",
    icon: ClipboardDocumentListIcon,
    permission: ["1", "2"],
    children: [
      {
        path: "/report/daily-sales-order",
        name: "Laporan Harian",
        id: "url-report-daily-sales-object",
        icon: ClipboardDocumentCheckIcon,
        permission: ["1", "2"],
      },
      {
        path: "/report/all-sales-order",
        name: "Laporan Semua Transaksi",
        id: "url-report-all-sales-order",
        icon: ClipboardDocumentCheckIcon,
        permission: ["1", "2"],
      },
    ],
  },
  {
    path: "/setting",
    name: "Pengaturan",
    id: "url-setting",
    icon: Cog6ToothIcon,
    permission: ["1", "2"],
    children: [
      {
        path: "/pengaturan",
        name: "Pengaturan",
        id: "url-setting",
        icon: WrenchScrewdriverIcon,
        permission: ["1"],
      },
      {
        path: "/user",
        name: "User",
        id: "url-user",
        icon: UsersIcon,
        permission: ["1", "2"],
      },
      {
        path: "/role",
        name: "Role & Permission",
        id: "url-role-permission",
        icon: ExclamationTriangleIcon,
        permission: ["0"],
      },
      {
        path: "/activity-log",
        name: "Activity Log",
        id: "url-activity-log",
        icon: BoltIcon,
        permission: ["0"],
      },
    ],
  },
];

const isActive = (path, item) => {
  // For parent items, only check if current path matches
  if (item.children) {
    return route.path === path;
  }

  // For child items, check if current path matches
  return route.path === path;
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
  toggleBlur();
};

const toggleBlur = () => {
  isBlur.value = isSidebarOpen.value;
  emit("toggleBlur", isBlur.value);

  // Add/remove overflow hidden from body when sidebar is open/closed
  if (isBlur.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
};

const closeSidebar = () => {
  if (isSidebarOpen.value) {
    isSidebarOpen.value = false;
    toggleBlur();
  }
};

// Close sidebar when clicking on a menu item on mobile
const handleMenuItemClick = () => {
  if (window.innerWidth < 768) {
    // Assuming 768px is your md breakpoint
    closeSidebar();
  }
};

// Close sidebar when clicking outside
const handleClickOutside = (event) => {
  const sidebar = document.getElementById("sidebar");
  const sidebarToggle = document.getElementById("open-sidebar");

  if (!isSidebarOpen.value) return;

  const clickedOutsideSidebar = sidebar && !sidebar.contains(event.target);
  const clickedOutsideToggle = !(
    sidebarToggle && sidebarToggle.contains(event.target)
  );

  if (clickedOutsideSidebar && clickedOutsideToggle) {
    closeSidebar();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.body.style.overflow = ""; // Reset overflow when component is unmounted
});

const toggleMenu = (id) => {
  openMenus[id] = !openMenus[id];
};

// Automatically open parent menus when child items are active
watch(
  () => route.path,
  () => {
    menuItems.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          (child) => route.path === child.path
        );
        openMenus[item.id] = hasActiveChild;
      }
    });
  },
  { immediate: true }
);

const filteredMenuItems = computed(() => {
  const roleId = localStorage.getItem("role_id");

  const checkPermission = (item) => {
    // Check current item's permission
    const hasItemPermission =
      item.permission.includes(roleId) ||
      roleId == "null" ||
      item.permission.includes("all");

    // If item has no children, return its own permission
    if (!item.children || item.children.length === 0) {
      return hasItemPermission;
    }

    // Filter children with permission
    const filteredChildren = item.children.filter(
      (child) =>
        child.permission.includes(roleId) ||
        roleId == "null" ||
        child.permission.includes("all")
    );

    // Parent is only considered to have permission if it has children with permission
    return filteredChildren.length > 0;
  };

  return menuItems.filter((item) => {
    // If an item has children, filter its children
    if (item.children && item.children.length > 0) {
      item.children = item.children.filter(
        (child) =>
          child.permission.includes(roleId) ||
          roleId == "null" ||
          child.permission.includes("all")
      );
      item.permission = ["0"];
    }
    // Check permission for the item
    return checkPermission(item);
  });
});

// Expose methods to parent component
defineExpose({
  toggleSidebar,
  closeSidebar,
  isSidebarOpen,
});
</script>

<style>
/* Modern sidebar styles - using Tailwind classes */
</style>
